import { type FunctionComponent, useState } from "react";
import { RatingStars } from "@App/Components/common/Icons";
import { ReviewItem } from "@App/Components/ReviewItem";
import { useReviews } from "@App/hooks/api/useReviews";
import { ReviewsSkeleton } from "@App/Components/Skeletons/ReviewsSkeleton";
import { useTranslation } from "react-i18next";

export const Reviews: FunctionComponent = () => {
  const { t } = useTranslation();
  const { loading, error, reviews } = useReviews();
  const [showAllReviews, setShowAllReviews] = useState(false);

  const formatRatingPercentage = (value) => {
    let result = 0;
    if (!reviews || reviews.length === 0) {
      return result;
    }
    reviews.map((review) => {
      result += review[value];
    });
    if (reviews.filter((review) => !!review[value]).length === 0) {
      return 0;
    }
    return result / reviews.filter((review) => !!review[value]).length;
  };

  const formatRating = (value) => {
    return ((formatRatingPercentage(value) / 100) * 5).toFixed(2).replace(".", ",");
  };

  if (loading) {
    return <ReviewsSkeleton />;
  }

  if (error) {
    return <div></div>;
  }

  if (!loading && !reviews) {
    <div></div>;
  }

  return (
    <section id="bewertungen" className="container mx-auto my-10 border-t border-neutral-100 pt-10">
      <div className={"text-2xl font-serif mb-5"}>{t("reviews.rating.overall")}</div>

      <span className="flex items-center text-neutral-700 font-normal no-underline">
        <RatingStars
          className={"mr-2"}
          type={"rating"}
          percent={formatRatingPercentage("rating")}
        />
        {formatRatingPercentage("rating").toFixed(0)}% {t("reviews.rating.rating")}
      </span>
      <span className={"text-sm text-neutral-700 mt-1"}>{t("reviews.rating.reference")}</span>

      <div className="md:flex gap-10 w-full my-10 mb-14">
        <div className={"md:w-1/2 mb-5"}>
          <div className={"text-xl font-serif mb-5"}>{t("reviews.customer-ratings")}</div>
          <table className="border-separate w-full my-5">
            <tbody>
              <tr>
                <td>
                  <div>{t("reviews.rating.cleanliness")}</div>
                </td>
                <td>
                  <div className="flex justify-end items-center text-neutral-700 font-normal">
                    <RatingStars
                      type={"cleanliness"}
                      percent={formatRatingPercentage("cleanliness")}
                    />
                    &nbsp;
                    {formatRating("cleanliness")}
                  </div>
                </td>
              </tr>

              <tr>
                <td>
                  <div>{t("reviews.rating.quality")}</div>
                </td>
                <td>
                  <div className="flex justify-end items-center text-neutral-700 font-normal">
                    <RatingStars type={"quality"} percent={formatRatingPercentage("quality")} />
                    &nbsp;
                    {formatRating("quality")}
                  </div>
                </td>
              </tr>

              <tr>
                <td>
                  <div>{t("reviews.rating.punctuality")}</div>
                </td>
                <td>
                  <div className="flex justify-end items-center text-neutral-700 font-normal">
                    <RatingStars
                      type={"punctuality"}
                      percent={formatRatingPercentage("punctuality")}
                    />
                    &nbsp;
                    {formatRating("punctuality")}
                  </div>
                </td>
              </tr>

              <tr>
                <td>
                  <div>{t("reviews.rating.competence")}</div>
                </td>
                <td>
                  <div className="flex justify-end items-center text-neutral-700 font-normal">
                    <RatingStars
                      type={"competence"}
                      percent={formatRatingPercentage("competence")}
                    />
                    &nbsp;
                    {formatRating("competence")}
                  </div>
                </td>
              </tr>

              <tr>
                <td>
                  <div>{t("reviews.rating.kindness")}</div>
                </td>
                <td>
                  <div className="flex justify-end items-center text-neutral-700 font-normal">
                    <RatingStars type={"kindness"} percent={formatRatingPercentage("kindness")} />
                    &nbsp;
                    {formatRating("kindness")}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          {reviews &&
            reviews
              .filter((review) => review.is_verified)
              .map((review, idx) => {
                return showAllReviews || idx < 2 ? (
                  <ReviewItem key={review.id} review={review} />
                ) : (
                  ""
                );
              })}
          {reviews && reviews.filter((review) => review.is_verified).length > 2 && (
            <button
              className={`md:hidden bg-warning text-white w-full py-3 px-4 rounded mt-3 font-bold max-w-[340px]`}
              onClick={() => setShowAllReviews(!showAllReviews)}
            >
              {showAllReviews ? t("reviews.show-less") : t("reviews.show-more")}
            </button>
          )}
        </div>

        <div className={"md:w-1/2 mb-5 md:pl-10 md:border-l border-neutral-100"}>
          <div className={"text-xl font-serif mb-5"}>{t("reviews.more-reviews")}</div>
          {reviews &&
            reviews
              .filter((review) => !review.is_verified)
              .map((review, idx) => {
                return showAllReviews || idx < 2 ? (
                  <ReviewItem key={review.id} review={review} />
                ) : (
                  ""
                );
              })}
          {reviews && reviews.filter((review) => !review.is_verified).length > 2 && (
            <button
              className={`bg-warning text-white w-full py-3 px-4 rounded mt-3 font-bold max-w-[340px]`}
              onClick={() => setShowAllReviews(!showAllReviews)}
            >
              {showAllReviews ? t("reviews.show-less") : t("reviews.show-more")}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
